import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Space, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import css from './ProductBrandsComponent.module.css';
import { ProductBrandsCreateModal } from '../settingSectionModals/ProductBrandsCreateModal/ProductBrandsCreateModal';
import { ProductBrandsUpdateModal } from '../settingSectionModals/ProductBrandsUpdateModal/ProductBrandsUpdateModal';
import { productBrandService } from '../../../services/settings-services';
import { productBrandsActions } from '../../../store/slices/settings-slices';

const ProductBrandsComponent = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const { productBrands, status, trigger } = useSelector(store => store.productBrands);

  const currentLanguage = i18n.language;
  const loading = status === 'Loading';

  useEffect(() => {
    dispatch(productBrandsActions.getAllProductBrands());
  }, [trigger, dispatch]);

  const handleDelete = async (id) => {
    try {
      await productBrandService.deleteById(id);
      dispatch(productBrandsActions.changeTrigger());
      toast.success(t('Brand deleted successfully'));
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast.error(t('Error deleting brand'));
    }
  };

  const handleUpdate = (record) => {
    setSelectedBrand(record);
    setIsUpdateModalOpen(true);
  };

  const handleCreate = () => {
    setIsCreateModalOpen(true);
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (text, record, index) => index + 1,
    },
    {
      title: t('Brand Name'),
      dataIndex: 'name',
      key: 'name',
      render: (name) => name[currentLanguage] || name['en'],
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleUpdate(record)}
            size="small"
          >
            {t('Edit')}
          </Button>
          <Popconfirm
            title={t('Are you sure you want to delete this brand?')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('Yes')}
            cancelText={t('No')}
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              {t('Delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <section className={css.wrap}>
      <div className={css.header}>
        <h2>{t('Product Brands')}</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
        >
          {t('Create New Brand')}
        </Button>
      </div>

      <div className={css.tableContainer}>
        <Table
          columns={columns}
          dataSource={productBrands}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `${t('Total')} ${total} ${t('items')}`,
          }}
          bordered
        />
      </div>

      {isCreateModalOpen && (
        <ProductBrandsCreateModal
          isOpen={isCreateModalOpen}
          setIsOpen={setIsCreateModalOpen}
        />
      )}

      {isUpdateModalOpen && (
        <ProductBrandsUpdateModal
          isOpen={isUpdateModalOpen}
          setIsOpen={setIsUpdateModalOpen}
          productBrand={selectedBrand}
        />
      )}
    </section>
  );
};

export default ProductBrandsComponent;
